/**
 * The recorder's last real pose per robot: where a ghost stands once a
 * robot stops reporting (twin slice 1, plan W3 step 8).
 *
 * After a reload this is the only source for that: the worker has
 * already replaced a silent robot with a stub, and this page never saw
 * the pose before it. While the page is open, `robot-receipts.ts`
 * remembers what it saw; presence takes whichever is newer.
 *
 * `{ supported: false }` (a 404 from an api-server older than the route)
 * is an answer, not an error: ghosts come from the session alone. It is
 * re-asked only every ten minutes, so a page left open across an
 * api-server upgrade picks the route up without hammering an old one.
 */
import { useQuery } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { fetchRobotLastPoses } from '../v1-client';
import type { RobotLastPose, RobotLastPosesResult } from '../twin';
import type { SiteId } from '../v1';

export const robotLastPosesKey = (siteId: string) => ['robot-last-poses', siteId] as const;

/** Re-read the recorder this often while the route answers. Its flush
 *  is every 5 s, but a ghost only matters once a robot has gone quiet,
 *  and the page's own memory covers everything it saw. */
export const LAST_POSES_REFRESH_MS = 60_000;
/** How often an api-server that answered 404 is asked again. */
export const LAST_POSES_UNSUPPORTED_RECHECK_MS = 10 * 60_000;

/** How long until the next read, given the last answer. */
export function lastPosesRefetchMs(last: RobotLastPosesResult | undefined): number {
  return last?.supported === false ? LAST_POSES_UNSUPPORTED_RECHECK_MS : LAST_POSES_REFRESH_MS;
}

export function useRobotLastPoses() {
  const siteId = useSafeSiteId();
  return useQuery<RobotLastPosesResult>({
    queryKey: robotLastPosesKey(siteId),
    queryFn: () => fetchRobotLastPoses(siteId as SiteId),
    enabled: Boolean(siteId),
    staleTime: LAST_POSES_REFRESH_MS / 2,
    refetchInterval: (query) => lastPosesRefetchMs(query.state.data),
  });
}

/** The recorded poses keyed by bare robot name, or undefined when the
 *  route is unsupported or has not answered. */
export function recordedPosesOf(
  result: RobotLastPosesResult | undefined,
): Readonly<Record<string, RobotLastPose>> | undefined {
  return result?.supported ? result.robots : undefined;
}
